import { Box, Divider } from "@mui/material";
import React from "react";
import EconomicOverview from "./economic-overview";
import PersonalData from "./personal-data";
import { i18n } from "@/utils/i18n";
import Attachments from "./attachments";
import { FormProvider, useForm } from "react-hook-form";
import SectionTitle from "@/components/ui/section-title";
import { Report } from "@/types/report";
import { useAcknowledgeComplement, useSaveReviewNotes, useSubmit, useToggleAttachmentCompletion } from "./hooks";
import useFormListeners from "@/hooks/useFormListeners";
import Header from "./header";
import Footer from "./footer";
import { Forms } from "@/types/form";
import AssetsAndLiabilities from "./form/assets-liabilities";
import Categories from "./form/categories";
import useLocale from "@/context/locale";
import ReviewNotes from "./review-notes";
import FileViewer from "@/components/common/modals/file-viewer";
import useFormStatus from "@/context/form";

interface Props {
    data: Report;
}

const Form: React.FC<Props> = ({ data }) => {
    useLocale();
    const [reviewNotes, setReviewNotes] = React.useState(data.reviewNotes ?? "");
    const setSaveDisabled = useFormStatus(state => state.setSaveDisabled);

    const methods = useForm<Forms["Report"]>({
        defaultValues: {
            incomings: [],
            outgoings: [],
            accounts: [],
            assets: [],
            debts: [],
        },
        values: {
            incomings: data?.incomings,
            outgoings: data?.outgoings,
            accounts: data.accounts,
            assets: data.assets,
            debts: data.debts,
        },
    });
    const {
        handleSubmit,
        formState: { isDirty, isSubmitting },
    } = methods;

    useFormListeners("report-form", isDirty, isSubmitting);

    const reset = methods.reset.bind(this, methods.getValues());

    const {
        name,
        attachments,
        principal,
        trustee,
        incomings,
        outgoings,
        accounts,
        startDate,
        endDate,
    } = data;

    const onSubmit = useSubmit();
    const { saving: savingReviewNotes, onSave: onSaveReviewNotes } = useSaveReviewNotes();
    const { acknowledging, onAcknowledge } = useAcknowledgeComplement();
    const { toggling: togglingCompletion, onToggle: onToggleCompletion } = useToggleAttachmentCompletion();
    const isReviewNotesDirty = reviewNotes !== (data.reviewNotes ?? "");

    return (
        <React.Fragment>
            <Header
                name={name}
                startDate={startDate}
                endDate={endDate}
                attachments={attachments}
                hasSectionMap={Boolean(data.sections?.length)}
            />
            <Box padding="30px 90px 30px 30px" display="flex" justifyContent="space-between">
                <PersonalData principal={principal} trustee={trustee} />
                <EconomicOverview
                    incomings={incomings}
                    outgoings={outgoings}
                    accounts={accounts}
                    startDate={startDate}
                    endDate={endDate}
                />
            </Box>
            <Divider />
            <Box padding="30px">
                <Attachments
                    attachments={attachments}
                    complementStatus={data.complementStatus}
                    onAcknowledgeComplement={onAcknowledge}
                    acknowledgingComplement={acknowledging}
                    onToggleAttachmentCompletion={onToggleCompletion}
                    togglingAttachmentCompletion={togglingCompletion}
                />
            </Box>
            <Divider />
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(async data => await onSubmit(data, reset))}
                    id="report-form"
                >
                    <Box padding="30px 30px 30px 60px">
                        <AssetsAndLiabilities startDate={startDate} endDate={endDate} />
                    </Box>
                    <Divider />
                    <Box display="flex" flexDirection="column" gap="40px">
                        <Box padding="30px 30px 30px 60px">
                            <SectionTitle title={i18n.t("preview")} />
                            <Categories variant="incomings" />
                        </Box>
                        <Divider />
                        <Box padding="30px 30px 30px 60px">
                            <Categories variant="outgoings" />
                        </Box>
                    </Box>
                </form>
                <Divider />
                <ReviewNotes
                    value={reviewNotes}
                    onChange={value => {
                        setReviewNotes(value);
                        setSaveDisabled(value !== (data.reviewNotes ?? ""));
                    }}
                    onSave={async () => {
                        const updated = await onSaveReviewNotes(reviewNotes);
                        if (updated?.reviewNotes !== undefined) {
                            setReviewNotes(updated.reviewNotes);
                            setSaveDisabled(false);
                        }
                    }}
                    saving={savingReviewNotes}
                    isDirty={isReviewNotesDirty}
                    attachments={attachments}
                />
                <Footer reviewNotes={reviewNotes} reviewStatus={data.reviewStatus} />
            </FormProvider>
            <FileViewer />
        </React.Fragment>
    );
};

export default Form;
